import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Home, FileText, Brain, MoreHorizontal, Target, Bot, Activity, LogOut, MessageSquare, Gamepad2, BookOpen, Dumbbell, Settings, Wind, Bell, HardDrive, Sparkles, Heart, Mail, Users, Utensils } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";
import { getMenuItems } from "../config/menuItems";
import Logo from "./Logo";
import NotificationBell from "./NotificationBell";

const PRIMARY_TABS = [
  { icon: Home, label: "Home", path: "/dashboard" },
  { icon: Target, label: "Goals", path: "/goals" },
  { icon: FileText, label: "Plans", path: "/plans" },
  { icon: Brain, label: "Training", path: "/training" },
  { icon: Activity, label: "Trends", path: "/wellness-trends" },
  { icon: Bot, label: "AI Coach", path: "/ai-coach" },
];

const MORE_ITEMS = [
  { icon: Wind, label: "Pre-Game Routines", path: "/pre-routines" },
  { icon: MessageSquare, label: "Messages", path: "/messages" },
  { icon: BookOpen, label: "Journal", path: "/journal" },
  { icon: Utensils, label: "Nutrition & Exercise", path: "/daily-log" },
  { icon: FileText, label: "News", path: "/news" },
  { icon: Bell, label: "Notifications", path: "/notifications" },
  { icon: HardDrive, label: "Storage", path: "/dashboard?storage=1" },
  { icon: Sparkles, label: "AI Insights", path: "/trends" },
  { icon: Heart, label: "Favorites", path: "/favorites" },
  { icon: Mail, label: "Weekly Report", path: "/dashboard?weeklyReport=1" },
  { icon: Users, label: "Get Support", path: "/messages?request_type=coach" },
  { icon: Dumbbell, label: "Physical Workouts", path: "/workouts" },
  { icon: Gamepad2, label: "Mindfulness Games", path: "/mindfulness-games" },
  { icon: Settings, label: "User Settings", path: "/settings" },
];

export default function MobileNavigation({ onLogout, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const adminMenu = getMenuItems(user?.role);
  const adminOnlyItems = adminMenu.filter(item =>
    ['/team-reports', '/athlete-overview', '/user-management'].includes(item.path)
  );

  const isActive = (path) => location.pathname === path;
  const isMoreActive = [...MORE_ITEMS, ...adminOnlyItems].some(item => isActive(item.path));

  return (
    <>
      {/* Top bar - slim */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-b border-gray-200 dark:border-gray-800 safe-top" data-testid="mobile-top-bar">
        <div className="flex items-center justify-between h-14 px-3">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
            <Logo size="small" className="!h-10 [&_img]:!h-10" />
            <span
              className="text-emerald-800 tracking-wide whitespace-nowrap font-bold"
              style={{ fontFamily: "'Playfair Display SC', serif", fontSize: '11px', fontWeight: 800, letterSpacing: '0.05em' }}
              data-testid="elite-tagline-mobile"
            >
              Elite Mental Performance Achieved
            </span>
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <button
              onClick={onLogout}
              className="flex items-center gap-1.5 text-gray-500 hover:text-gray-700 active:scale-95 transition-all px-3 py-2 rounded-lg"
              data-testid="mobile-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-xs font-medium">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Bottom tab bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-t border-gray-200 dark:border-gray-800 safe-bottom" data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around h-16 px-1">
          {PRIMARY_TABS.map((tab) => (
            <button
              key={tab.path}
              onClick={() => navigate(tab.path)}
              className={`flex flex-col items-center justify-center flex-1 h-full gap-0.5 transition-colors active:scale-95 ${
                isActive(tab.path)
                  ? 'text-emerald-600'
                  : 'text-gray-400'
              }`}
              data-testid={`mobile-tab-${tab.label.toLowerCase()}`}
            >
              <tab.icon className={`w-5 h-5 ${isActive(tab.path) ? 'stroke-[2.5]' : ''}`} />
              <span className="text-[10px] font-semibold">{tab.label}</span>
              {isActive(tab.path) && (
                <div className="absolute bottom-1 w-6 h-0.5 bg-emerald-600 rounded-full" />
              )}
            </button>
          ))}

          {/* More tab */}
          <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
            <SheetTrigger asChild>
              <button
                className={`flex flex-col items-center justify-center flex-1 h-full gap-0.5 transition-colors active:scale-95 ${
                  isMoreActive ? 'text-emerald-600' : 'text-gray-400'
                }`}
                data-testid="mobile-tab-more"
              >
                <MoreHorizontal className={`w-5 h-5 ${isMoreActive ? 'stroke-[2.5]' : ''}`} />
                <span className="text-[10px] font-semibold">More</span>
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="rounded-t-2xl max-h-[70vh]" data-testid="mobile-more-sheet">
              <SheetHeader>
                <SheetTitle className="text-left text-lg font-bold">More</SheetTitle>
              </SheetHeader>
              <div className="grid grid-cols-3 gap-3 py-4">
                {MORE_ITEMS.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => { navigate(item.path); setMoreOpen(false); }}
                    className={`flex flex-col items-center gap-2 p-3 rounded-xl transition-colors active:scale-95 ${
                      isActive(item.path)
                        ? 'bg-emerald-50 text-emerald-600'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid={`mobile-more-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <item.icon className="w-6 h-6" />
                    <span className="text-[11px] font-medium text-center leading-tight">{item.label}</span>
                  </button>
                ))}
                {adminOnlyItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => { navigate(item.path); setMoreOpen(false); }}
                    className={`flex flex-col items-center gap-2 p-3 rounded-xl transition-colors active:scale-95 ${
                      isActive(item.path)
                        ? 'bg-emerald-50 text-emerald-600'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid={`mobile-more-${item.label.toLowerCase().replace(/[\s\n]/g, '-')}`}
                  >
                    <item.icon className="w-6 h-6" />
                    <span className="text-[11px] font-medium text-center leading-tight">{item.label.replace(/\n/g, ' ')}</span>
                  </button>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </>
  );
}
